<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { Tabs } from '@/components/ui/tabs';
    import type { Employee, SharedData } from '@/types';
    import { useEmployeeChangesData } from '@/composables/useEmployeeChangesData';
    import { useCompanyData } from '@/composables/useCompany';
    import { usePage } from '@inertiajs/vue3';
    import PersonalTab from './changes-tabs/PersonalTab.vue';
    import AddressTab from './changes-tabs/AddressTab.vue';
    import EmploymentTab from './changes-tabs/EmploymentTab.vue';
    import BankTab from './changes-tabs/BankTab.vue';
    import CompanyPensionTab from './changes-tabs/CompanyPensionTab.vue';
    import OptionalTab from './changes-tabs/OptionalTab.vue';
    import TerminationTab from './changes-tabs/TerminationTab.vue';
    import ChangedFieldsTab from './changes-tabs/ChangedFieldsTab.vue';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const { companyData } = useCompanyData(selectedCompanyId.value);

    const props = defineProps<{
        employee: Employee;
    }>();

    const company = computed(() => ({
        ...props.employee.company,
        ...companyData.value,
    }));

    const { employeeChangesData, isLoadingEmployeeChangesData } = useEmployeeChangesData(
        props.employee.id,
    );

    const hasUnnotedChanges = computed(() => {
        return (
            (props.employee.log && !props.employee.log.changes_noted) ||
            props.employee?.unnoted_salary
        );
    });

    const showOptional = computed(() => {
        if (isLoadingEmployeeChangesData.value) return false;

        const old = employeeChangesData.value?.optional?.old;
        if (!old) return false;

        const isset = (key: string) => old[key] !== undefined && old[key] !== null;

        return (
            isset('health_insurance') ||
            isset('diaeter') ||
            isset('kilometer') ||
            isset('has_pension') ||
            isset('danmarks_statistik') ||
            isset('has_free_car') ||
            isset('has_free_phone') ||
            isset('has_lunch_arrangement')
        );
    });

    const tabs = computed(() => {
        return [
            ...(hasUnnotedChanges.value
                ? [{ value: 'changes', label: 'employee.changed.header' }]
                : []),
            { value: 'personal', label: 'employee.personal.header' },
            { value: 'address', label: 'employee.address.header' },
            { value: 'employment', label: 'employee.employment.header' },
            { value: 'bank', label: 'employee.bank.header' },
            { value: 'pension', label: 'employee.pension.company_headline' },
            ...(showOptional.value
                ? [{ value: 'optional', label: 'employee.optional.header' }]
                : []),
            { value: 'termination', label: 'employee.termination.header' },
        ];
    });

    const currentTab = ref(hasUnnotedChanges.value ? 'changes' : 'personal');
</script>

<template>
    <Tabs
        :tabs="tabs"
        v-model="currentTab"
    >
        <template
            #changes
            v-if="hasUnnotedChanges"
        >
            <ChangedFieldsTab
                :employee="employee"
                :data="employeeChangesData?.changedFields"
            />
        </template>
        <template #personal>
            <PersonalTab
                :employee="employee"
                :data="employeeChangesData?.personal"
                :optional="employeeChangesData?.optional"
                :company="company"
            />
        </template>
        <template #address>
            <AddressTab
                :employee="employee"
                :data="employeeChangesData?.address"
            />
        </template>
        <template #employment>
            <EmploymentTab
                :employee="employee"
                :data="employeeChangesData?.employment"
                :personalData="employeeChangesData?.personal"
                :oldEmployment="employeeChangesData?.oldEmployment"
            />
        </template>
        <template #bank>
            <BankTab
                :employee="employee"
                :data="employeeChangesData?.bank"
            />
        </template>
        <template #pension>
            <CompanyPensionTab
                :employee="employee"
                :data="employeeChangesData?.pension"
            />
        </template>
        <template
            #optional
            v-if="showOptional"
        >
            <OptionalTab
                :employee="employee"
                :data="employeeChangesData?.optional"
            />
        </template>
        <template #termination>
            <TerminationTab
                :employee="employee"
                :data="employeeChangesData?.termination"
            />
        </template>
    </Tabs>
</template>
